define('presentation/signup/viewModels/signupEmailVerifyViewModel',[
    'common/promises/promiseFactory',
    'constants/signupEmailVerificationConstants',
    'i18next',
    'presentation/signup/common/signupNavigationManager',
    'presentation/signup/facades/signupEmailFacade',
    'presentation/signup/validators/signupEmailVerificationValidator'
], function() {

    return function() {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');
        const _signupEmailVerificationConstants = require('constants/signupEmailVerificationConstants');

        const RESEND_COOLDOWN_SECONDS = 25;

        let _facade = null;
        let _verificationValidator = null;
        let _signupNavigationManager = null;
        let _disposables = [];
        let _resendCooldownTimer = null;

        const _isResendInProgress = ko.observable(false);

        self.userEmail = ko.observable('');

        self.headingText = ko.observable(_i18n.t('signupEmailVerify:headingText'));
        self.subtitleText = ko.pureComputed(() =>
            _i18n.t('signupEmailVerify:subtitleText', { userEmail: self.userEmail() }));
        self.emailVerificationCodeDigits = ko.observableArray([
            { digit: ko.observable('') },
            { digit: ko.observable('') },
            { digit: ko.observable('') },
            { digit: ko.observable('') },
            { digit: ko.observable('') },
            { digit: ko.observable('') }
        ]);

        self.isCompositionComplete = ko.observable(false);
        self.isVerificationStepValid = ko.observable(false);
        self.isMaxRetryCountReached = ko.observable(false);
        self.showIncorrectVerificationCodeError = ko.observable(false);
        self.verificationCodeSendCount = ko.observable(0);
        self.verificationFailureCount = ko.observable(0);
        self.showResendEmailSpinner = ko.observable(false);
        self.resendCooldownSeconds = ko.observable(0);
        self.isNavigateToPreviousPageVisible = ko.observable(true);

        self.emailVerificationCode = ko.pureComputed(() => {
            return self.emailVerificationCodeDigits().map(d => d.digit()).join('');
        });

        self.incorrectVerificationCodeErrorKey = ko.pureComputed(() => {
            return self.hasReachedMaxFailures() ? "signupEmail:maxVerificationAttemptsReached" : "signupEmail:incorrectVerificationCode";
        });

        self.hasReachedMaxFailures = ko.pureComputed(() => {
            return self.verificationFailureCount() >= _signupEmailVerificationConstants.MaxVerificationAttempts;
        });

        self.isVerificationArrowValid = ko.pureComputed(() => {
            return self.isVerificationStepValid() && !self.hasReachedMaxFailures();
        });

        self.showResendCooldown = ko.pureComputed(() => {
            return self.resendCooldownSeconds() > 0;
        });

        self.resendEmailLinkText = ko.pureComputed(() => {
            return self.showResendCooldown() ?
                _i18n.t("signupEmail:resendIn", { seconds: self.resendCooldownSeconds() }) :
                _i18n.t("signupEmail:resendEmail");
        });

        self.isResendEmailDisabled = ko.pureComputed(() => {
            return self.isMaxRetryCountReached() || self.showResendCooldown();
        });

        const _updateIsMaxRetryCountReached = (resendCount, verificationAttempts) => {
            self.isMaxRetryCountReached(
                resendCount >= _signupEmailVerificationConstants.MaxVerificationCodeSendCount ||
                verificationAttempts >= _signupEmailVerificationConstants.MaxVerificationAttempts
            );
        };

        const _handleVerificationCodeChanged = () => {
            self.showIncorrectVerificationCodeError(false);
            const isVerificationStepValid = self.emailVerificationCodeDigits().every(d => d.digit() !== '');
            self.isVerificationStepValid(isVerificationStepValid);
        };

        const _startResendCooldown = () => {
            if (_resendCooldownTimer !== null) {
                clearInterval(_resendCooldownTimer);
                _resendCooldownTimer = null;
            }
            self.resendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
            _resendCooldownTimer = setInterval(() => {
                const remaining = self.resendCooldownSeconds() - 1;
                if (remaining <= 0) {
                    clearInterval(_resendCooldownTimer);
                    _resendCooldownTimer = null;
                    self.resendCooldownSeconds(0);
                } else {
                    self.resendCooldownSeconds(remaining);
                }
            }, 1000);
        };

        const _sendVerificationCode = () => {
            self.showResendEmailSpinner(true);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                if (_isResendInProgress() || self.isMaxRetryCountReached() || self.showResendCooldown()) {
                    self.showResendEmailSpinner(false);
                    deferredObject.resolve();
                    return;
                }

                _isResendInProgress(true);
                _facade.sendEmailVerificationCode()
                    .fail(() => {
                        _isResendInProgress(false);
                        self.showResendEmailSpinner(false);
                        self.isMaxRetryCountReached(true);
                        deferredObject.resolve();
                    })
                    .done((response) => {
                        _isResendInProgress(false);
                        self.showResendEmailSpinner(false);

                        self.verificationCodeSendCount(response.emailVerificationCodeSendCount);
                        self.verificationFailureCount(response.emailVerificationFailureCount);

                        _updateIsMaxRetryCountReached(response.emailVerificationCodeSendCount, response.emailVerificationFailureCount);
                        _startResendCooldown();
                        deferredObject.resolve();
                    });
            });
        };

        const _handleVerificationCodeSubmit = () => {
            return _promiseFactory.defer((deferredObject) => {
                _verificationValidator.validate()
                    .fail(deferredObject.reject)
                    .done((isValid) => {
                        if (!isValid) {
                            deferredObject.resolve(false);
                            return;
                        }

                        const userEmail = self.userEmail();
                        const verificationCode = self.emailVerificationCode();

                        _facade.setEmailStepCompleted(userEmail, verificationCode)
                            .fail(deferredObject.reject)
                            .done((result) => {
                                self.verificationCodeSendCount(result.emailVerificationCodeSendCount);
                                self.verificationFailureCount(result.emailVerificationFailureCount);

                                _updateIsMaxRetryCountReached(result.emailVerificationCodeSendCount, result.emailVerificationFailureCount);

                                if (!result.isEmailCompleted) {
                                    if (result.emailVerificationFailureCount >= _signupEmailVerificationConstants.MaxVerificationAttempts) {
                                        self.showIncorrectVerificationCodeError(false);
                                    } else {
                                        self.showIncorrectVerificationCodeError(true);
                                    }

                                    _verificationValidator.validate();
                                    deferredObject.resolve(false);
                                    return;
                                }

                                self.showIncorrectVerificationCodeError(false);

                                deferredObject.resolve(true);
                            });
                    });
            });
        };

        self.handleKeyUp = (data, event) => {
            const input = event.target;
            const index = parseInt(input.id.split('-')[2]);

            if (input.value.length === 1 && index < 5) {
                const nextInput = document.getElementById(`otp-input-${index + 1}`);
                if (input && index < 5 && nextInput) {
                    nextInput.focus();
                }
            }

            if (event.key === "Backspace" && index > 0) {
                const prevInput = document.getElementById(`otp-input-${index - 1}`);
                if (prevInput) {
                    prevInput.focus();
                }
            }
        };

        self.handlePaste = (data, event) => {
            navigator.clipboard.readText()
                .then((clipboardContents) => {
                    if (!clipboardContents) {
                        return;
                    }
                    const digitsOnly = clipboardContents.replace(/\D/g, '').slice(0, 6);
                    if (!digitsOnly) {
                        return;
                    }
                    event.preventDefault();
                    digitsOnly.split('').forEach((char, index) => {
                        if (index < 6) {
                            self.emailVerificationCodeDigits()[index].digit(char);
                        }
                    });
                    const nextInput = document.getElementById(`otp-input-${digitsOnly.length - 1}`);
                    if (nextInput) {
                        nextInput.focus();
                    }
                });
        };

        self.onForwardArrowClicked = () => _handleVerificationCodeSubmit();

        self.onBackArrowClicked = () => {
            return _promiseFactory.defer((deferredObject) => {
                _signupNavigationManager.previousPage();
                deferredObject.resolve();
            });
        };

        self.onResendEmailClicked = () => {
            _sendVerificationCode();
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _signupNavigationManager.detached();
            if (_resendCooldownTimer !== null) {
                clearInterval(_resendCooldownTimer);
                _resendCooldownTimer = null;
            }
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/signup/facades/signupEmailFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/signup/validators/signupEmailVerificationValidator');
            _verificationValidator = new ValidatorConstructor();
            _verificationValidator.registerViewModel(self);

            const SignupNavigationManagerConstructor = require('presentation/signup/common/signupNavigationManager');
            _signupNavigationManager = new SignupNavigationManagerConstructor();
            _signupNavigationManager.init();

            return _initialize();
        };

        const _initialize = () => {
            _disposables.push(self.emailVerificationCode.subscribe(_handleVerificationCodeChanged));

            _promiseFactory.deferIndefinitely((initializePromise) => {
                _facade.loadSignupSession()
                    .fail(initializePromise.reject)
                    .done((signupEmailPresentationObject) => {
                        const { emailAddress, emailVerificationCodeSendCount, emailVerificationFailureCount } = signupEmailPresentationObject;

                        self.userEmail(emailAddress);
                        self.verificationCodeSendCount(emailVerificationCodeSendCount);
                        self.verificationFailureCount(emailVerificationFailureCount);
                        _updateIsMaxRetryCountReached(emailVerificationCodeSendCount, emailVerificationFailureCount);

                        initializePromise.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});

